import React, { useState, useEffect } from 'react';
import type { UdaTheme, UdaStat } from '../udaThemes';
import { darken, udaCode, accentVars } from '../udaThemes';
import { usePortal } from '../PortalContext';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { KeyServicesSection } from './KeyServicesSection';
import { CitizenServicesSection } from './CitizenServicesSection';
import { AnnouncementSection } from './AnnouncementSection';
import { GallerySection } from './GallerySection';

const AP_EMBLEM = 'https://cdma.ap.gov.in/assets/logos/ap_state_logo_new.png';

/* Hero dwell time. Kept in sync with the .hero-progress animation (6s) in
   index.css so the progress bar lands exactly as the slide flips. */
const HERO_INTERVAL = 6000;

/* How long the track takes to scroll from one image to the next. */
const SLIDE_MS = 800;

/* The hero lockup is set in a serif, matching the official UDA sites — the
   rest of the portal stays on the sans body face. */
const SERIF = "Georgia, 'Times New Roman', 'Noto Serif', serif";

interface Props {
  theme: UdaTheme;
  onBackToDtcp: () => void;
}

/** One carousel slide: the image plus the caption the admin entered for it. */
interface HeroSlide {
  url: string;
  title: string;
  description: string;
}

// A seed script once wrote a JSON metadata blob into dscn_tx, which would render
// raw as the caption. Never show a serialized object to a citizen — blank it.
// (Same guard as HeroSection uses on the DTCP home.)
const plainText = (s: string) => (/^\s*[{[]/.test(s) ? '' : s);

/* Entrance for hero/caption elements. Applied inline rather than as a Tailwind
   arbitrary value because the cubic-bezier commas don't survive that syntax. */
const enter = (delay: number): React.CSSProperties => ({
  animation: 'fade-in-up 700ms cubic-bezier(0.22,1,0.36,1) both',
  animationDelay: `${delay}ms`,
});

/**
 * Count a numeric stat up from zero the first time it scrolls into view.
 * Values are free text ("1,721 km²", "5.2 Lakh", "—"), so only a leading
 * number is animated and the original formatting — grouping separators,
 * decimal places and the unit suffix — is rebuilt around it. Anything without
 * a leading number is returned untouched.
 */
const useCountUp = (value: string, run: boolean): string => {
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    const match = /^([\d,]+(?:\.\d+)?)(.*)$/.exec(value.trim());
    const target = match ? parseFloat(match[1].replace(/,/g, '')) : NaN;
    if (!run || !match || !isFinite(target)) {
      setDisplay(value);
      return;
    }

    const raw = match[1];
    const suffix = match[2];
    const decimals = raw.includes('.') ? raw.split('.')[1].length : 0;
    const grouped = raw.includes(',');
    const format = (n: number) => {
      const fixed = n.toFixed(decimals);
      const out = grouped
        ? Number(fixed).toLocaleString('en-IN', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          })
        : fixed;
      return `${out}${suffix}`;
    };

    const start = performance.now();
    let frame = requestAnimationFrame(function tick(now: number) {
      const p = Math.min((now - start) / 1100, 1);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setDisplay(p === 1 ? value : format(target * eased));
      if (p < 1) frame = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(frame);
  }, [value, run]);

  return display;
};

/** One key-figure tile in the stats strip. */
const StatCard: React.FC<{
  stat: UdaStat;
  accent: string;
  accentDark: string;
  run: boolean;
}> = ({ stat, accent, accentDark, run }) => {
  const value = useCountUp(stat.value, run);
  return (
    <div className="stagger-child group relative overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-[0_6px_22px_rgba(0,0,0,0.06)] p-4 sm:p-5 transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_18px_40px_rgba(0,0,0,0.12)]">
      {/* Accent bloom that fades in on hover */}
      <span
        className="pointer-events-none absolute -right-8 -top-10 w-28 h-28 rounded-full opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-20"
        style={{ background: accent }}
      />
      {/* Top hairline that draws across on hover */}
      <span
        className="pointer-events-none absolute inset-x-0 top-0 h-[3px] origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100"
        style={{ background: `linear-gradient(90deg, ${accent}, ${accentDark})` }}
      />
      <div className="relative flex items-center gap-3 sm:gap-4">
        <div
          className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
          style={{
            background: `linear-gradient(135deg, ${accent}26, ${accent}0D)`,
            color: accent,
            boxShadow: `inset 0 0 0 1px ${accent}26`,
          }}
        >
          <i className={`${stat.icon} text-xl sm:text-2xl`} />
        </div>
        <div className="min-w-0">
          <div className="text-lg sm:text-2xl font-extrabold text-gray-800 leading-tight truncate tabular-nums">
            {value}
          </div>
          <div className="text-[11px] sm:text-xs text-gray-500 font-medium truncate">{stat.label}</div>
        </div>
      </div>
    </div>
  );
};

/**
 * Dedicated, per-UDA landing page. Distinct from the DTCP home: a themed
 * identity hero (emblem + name + accent), a key-stats strip and an
 * "About this UDA" block, followed by the shared data-driven sections (which
 * already load this UDA's content by siteId), then a themed local-contact
 * footer strip. The accent colour flows through CSS variables so every UDA
 * looks different from one component.
 */
export const UdaLanding: React.FC<Props> = ({ theme, onBackToDtcp }) => {
  const accent = theme.accent;
  const accentDark = darken(accent, 0.28);
  const { publicApi } = usePortal();

  // Hero images and stats come from the API per siteId; the curated values in
  // udaThemes are the fallback (and what shows while the API is unreachable).
  // The hero cycles through EVERY homepage_carousel image for this UDA — it
  // previously kept only the first one and rendered it as a static backdrop.
  // Each slide carries the title (imge_nm) and description (dscn_tx) the admin
  // typed in Manage Carousel, shown as a caption on slides after the first.
  const [heroImages, setHeroImages] = useState<HeroSlide[]>([
    { url: theme.heroImage, title: '', description: '' },
  ]);
  const [stats, setStats] = useState<UdaStat[]>(theme.stats);

  // The carousel is ONE track of images laid edge to edge, scrolled by
  // translating the whole strip. Slides used to be independently positioned
  // layers that slid past each other, which could leave the page background
  // showing between them; laid out in a single flex row they are physically
  // adjacent, so no gap is possible.
  //
  // `index` is a position on that track, and the track carries a copy of the
  // first image at the end: advancing past the last image scrolls onto the copy
  // (still moving forwards, no rewind), then the position is snapped back to 0
  // with the transition off — which looks identical, because the copy IS slide
  // one. `snap` is that transition-off flag.
  const [index, setIndex] = useState(0);
  const [snap, setSnap] = useState(false);

  const slideCount = heroImages.length;
  const track = slideCount > 1 ? [...heroImages, heroImages[0]] : heroImages;
  const current = index % slideCount; // the real image on screen

  const statsReveal = useScrollReveal({ threshold: 0.2 });
  const aboutReveal = useScrollReveal({ threshold: 0.12 });

  useEffect(() => {
    // Reset to this UDA's curated defaults whenever the site changes.
    setHeroImages([{ url: theme.heroImage, title: '', description: '' }]);
    setIndex(0);
    setSnap(false);
    setStats(theme.stats);

    // Hero images: every carousel image for this site, in sequence order.
    publicApi
      .get(`/gallery/images/homepage_carousel?siteId=${theme.id}`)
      .then((res) => {
        const slides: HeroSlide[] = (res.data?.data || [])
          .filter((i: any) => i.imge_url_tx)
          .map((i: any) => ({
            url: i.imge_url_tx,
            title: i.imge_nm || '',
            description: plainText(i.dscn_tx || ''),
          }));
        if (slides.length > 0) setHeroImages(slides);
      })
      .catch(() => {}); // keep fallback

    // Key stats for this UDA.
    publicApi
      .get(`/uda-stats?siteId=${theme.id}`)
      .then((res) => {
        const rows = res.data?.data || [];
        if (rows.length > 0) {
          setStats(
            rows.map((r: any) => ({
              label: r.stat_lbl_tx || '',
              value: r.stat_val_tx || '',
              icon: r.icn_tx || 'ri-bar-chart-2-line',
            })),
          );
        }
      })
      .catch(() => {}); // keep fallback
  }, [theme.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-advance the hero, continuously — deliberately NOT paused on hover, so
  // the carousel keeps scrolling even while the pointer rests over it. A
  // functional update keeps this independent of `index`, so the interval is
  // armed once per image-set rather than torn down and rebuilt on every slide.
  useEffect(() => {
    if (slideCount <= 1) return;
    const timer = setInterval(() => setIndex((i) => i + 1), HERO_INTERVAL);
    return () => clearInterval(timer);
  }, [slideCount]);

  // The image set arrives from the API after first paint, so the track can grow
  // (or shrink) under a live index. Rewind to the start when it does, otherwise
  // the position could point past the end of the strip and show blank.
  useEffect(() => {
    setSnap(true);
    setIndex(0);
  }, [slideCount]);

  // Landed on the trailing copy of slide one: once it has finished scrolling
  // into place, jump the track back to the real slide one with the transition
  // off. Nothing moves on screen — the two images are identical.
  useEffect(() => {
    if (slideCount <= 1 || index !== slideCount) return;
    const t = setTimeout(() => {
      setSnap(true);
      setIndex(0);
    }, SLIDE_MS + 40);
    return () => clearTimeout(t);
  }, [index, slideCount]);

  // Re-arm the transition after a snap, once the browser has painted the new
  // position — otherwise the next move would animate from the pre-snap spot.
  useEffect(() => {
    if (!snap) return;
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setSnap(false)));
    return () => cancelAnimationFrame(id);
  }, [snap]);

  const goNext = () => setIndex((i) => (i >= slideCount ? i : i + 1));

  // Stepping back from the first image: hop (transition off) to the trailing
  // copy, which looks identical, then scroll left from there.
  const goPrev = () => {
    if (slideCount <= 1) return;
    if (index !== 0) {
      setIndex((i) => i - 1);
      return;
    }
    setSnap(true);
    setIndex(slideCount);
    setTimeout(() => setIndex(slideCount - 1), 80);
  };

  // Set the accent vars on this wrapper too, so the component themes correctly
  // even if rendered outside the HomeContent root (which also sets them).
  const cssVars = accentVars(accent) as React.CSSProperties;

  const code = udaCode(theme.slug, theme.name);

  // The mark keeps the proportions of the reference lockup — a fixed 12.5vw,
  // so every UDA reads at the same optical size. Even the longest code
  // ("VK-PCPIR", 8 chars) runs to ~74vw and stays inside the gutters.
  const codeSize = 'clamp(52px, 12.5vw, 190px)';

  // The authority name, by contrast, IS fitted to the screen: names run from
  // 32 characters ("Eluru Urban Development Authority") to 76 (VK-PCPIR's), so
  // one fixed size would either overflow or leave the short ones stranded. A
  // serif capital advances ≈0.66em at this tracking, so 76vw / (0.66 × chars)
  // runs the line to about three-quarters of the width — sitting clearly
  // beneath the mark rather than competing with it.
  const nameSize = `clamp(10px, ${(76 / (0.66 * Math.max(theme.fullName.length, 1))).toFixed(2)}vw, 42px)`;

  const mapsHref = `https://www.google.com/maps?q=${theme.lat},${theme.lng}`;
  const phone = '0863-2000662';
  const email = `${theme.slug}-uda@ap.gov.in`;

  const focusAreas = [
    { icon: 'ri-file-shield-2-line', label: 'Building Permissions' },
    { icon: 'ri-layout-grid-line', label: 'Layout Approvals' },
    { icon: 'ri-map-2-line', label: 'Master Plan' },
    { icon: 'ri-road-map-line', label: 'Public Infrastructure' },
  ];

  const glanceRows = [
    { icon: 'ri-government-line', k: 'Authority', v: theme.name },
    { icon: 'ri-map-pin-2-line', k: 'District', v: theme.district },
    { icon: 'ri-compass-3-line', k: 'Region', v: theme.region },
    { icon: 'ri-calendar-check-line', k: 'Established', v: theme.established },
    ...stats.map((s) => ({ icon: s.icon, k: s.label, v: s.value })),
  ];

  return (
    <div style={cssVars}>
      {/* Accent top hairline so the whole page reads as "this UDA" */}
      <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${accent}, ${accentDark})` }} />

      {/* ====== IDENTITY HERO ====== */}
      <section
        className="relative w-full overflow-hidden"
        style={{ minHeight: 'clamp(440px, 66vh, 660px)' }}
      >
        {/* The track: every image for this UDA laid edge to edge in one strip,
            scrolled by translating the strip itself. Because the slides are
            siblings in a flex row there is no space between them — the next
            photograph is already touching the current one before it moves. The
            active slide holds a slow Ken Burns push, applied to an inner layer
            so it doesn't fight the track's transform. */}
        <div
          className="absolute inset-0 flex"
          style={{
            transform: `translate3d(-${index * 100}%, 0, 0)`,
            transition: snap ? 'none' : `transform ${SLIDE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
            willChange: 'transform',
          }}
        >
        {track.map((img, i) => {
          // Slide 1 is the identity title card; every later image carries the
          // title/description entered in Manage Carousel. The trailing copy of
          // slide one (i === slideCount) is an identity card too.
          const isIdentity = i % slideCount === 0;
          const showCaption = !isIdentity && !!(img.title || img.description);
          return (
            <div
              key={`${img.url}-${i}`}
              className="relative flex-none w-full h-full overflow-hidden"
              aria-hidden={i !== index}
            >
              {/* Photograph */}
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `url('${img.url}')`,
                  backgroundSize: '100% 100%',
                  backgroundPosition: 'center',
                }}
              />

              {/* No dark scrim over the photo — carousel images must show at full
                  clarity (same as the DTCP hero). Caption legibility comes from
                  the text drop-shadows; only a thin bottom fade remains so the
                  slide dots/counter stay visible. */}
              <div
                className="absolute inset-x-0 bottom-0 h-20"
                style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.35) 0%, transparent 100%)' }}
              />
              {/* Soft centre spotlight for the identity slide — lifts the
                  lettering off a busy photograph without flattening the corners. */}
              {isIdentity && (
                <div
                  className="absolute inset-0"
                  style={{ background: 'radial-gradient(ellipse at 50% 52%, rgba(0,0,0,0.30) 0%, transparent 62%)' }}
                />
              )}

              {/* Identity lockup — FIRST slide only. Every later image in the
                  carousel is shown unobstructed, so the branding reads as a
                  title card rather than a permanent watermark over every photo.
                  It lives INSIDE the slide, so the lettering travels with its
                  own photograph instead of hanging over the whole carousel. */}
              {isIdentity && (
                <div className="relative h-full portal-container flex flex-col items-center justify-center text-center py-14 sm:py-20 pointer-events-none">
                  {/* State emblem — sits directly on the photograph. No glass
                      disc or backdrop behind it: the emblem is already a
                      self-contained crest and a plate around it read as a grey
                      blob. The entrance stagger plays on first paint; later
                      returns to this slide are carried by the slide motion. */}
                  <img
                    src={AP_EMBLEM}
                    alt="Andhra Pradesh State Emblem"
                    className="w-24 h-24 sm:w-32 sm:h-32 lg:w-40 lg:h-40 object-contain drop-shadow-[0_2px_10px_rgba(0,0,0,0.45)]"
                    style={enter(0)}
                    onError={(e) => { (e.currentTarget.style.display = 'none'); }}
                  />

                  {/* UDA code — the headline mark, in the serif of the official
                      sites. Near-zero tracking so the letters sit tight. */}
                  <h1
                    className="text-white leading-[0.92] tracking-[0.01em] mt-4 sm:mt-6 whitespace-nowrap drop-shadow-[0_4px_20px_rgba(0,0,0,0.5)]"
                    style={{ fontFamily: SERIF, fontWeight: 700, fontSize: codeSize, ...enter(90) }}
                  >
                    {code}
                  </h1>

                  {/* Official authority name — fullName ("Rajahmundry Urban
                      Development Authority"), not name ("Rajahmundry UDA"),
                      which would just repeat the code above it. Same serif, set
                      to run the full width of the screen beneath the mark. No
                      accent rule between the two: the reference lockup is
                      emblem → mark → name and nothing else. */}
                  <p
                    className="uppercase leading-tight whitespace-nowrap mt-4 sm:mt-6 tracking-[0.06em]"
                    style={{
                      fontFamily: SERIF,
                      fontWeight: 500,
                      fontSize: nameSize,
                      // Pale gold, as in the reference lockup — a warm
                      // counterpoint to the white mark. The dark shadow keeps it
                      // legible on a bright sky; the faint amber one gives it
                      // the same glow.
                      color: '#F0DCA5',
                      textShadow: '0 2px 12px rgba(0,0,0,0.55), 0 0 22px rgba(214,164,60,0.35)',
                      ...enter(180),
                    }}
                  >
                    {theme.fullName}
                  </p>

                  {/* The tagline and the region/district pill deliberately do
                      NOT appear here — the hero carries the emblem and the
                      authority's identity only, so the photograph stays
                      uncluttered. Both values are still shown in the "About this
                      UDA" section further down the page. */}
                </div>
              )}

              {/* Per-image caption, also inside the slide so it travels with its
                  photograph. No entrance stagger here: the slide motion IS the
                  entrance, and animating the text on top of it reads as busy. */}
              {showCaption && (
                <div className="relative h-full flex items-center pointer-events-none">
                  <div className="portal-container w-full">
                    {/* The left inset clears the prev arrow: below ~1536px the
                        container's gutter is narrower than the arrow, so without
                        it the arrow lands on top of the title. */}
                    <div className={`max-w-xl text-left ${heroImages.length > 1 ? 'sm:ml-14 2xl:ml-0' : ''}`}>
                      {/* Accent rule + UDA label. White glass rather than
                          accent-on-accent: the tinted pill used before rendered
                          dark-blue text on a dark-blue wash over a photograph
                          and was unreadable. */}
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-9 h-[3px] rounded-full" style={{ background: accent }} />
                        <span className="inline-flex items-center gap-1.5 uppercase tracking-[2px] font-bold text-[11px] sm:text-xs text-white bg-white/15 backdrop-blur-md ring-1 ring-white/30 px-3.5 py-1.5 rounded-full">
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent, boxShadow: `0 0 0 3px ${accent}40` }} />
                          {theme.shortName}
                        </span>
                      </div>

                      {img.title && (
                        <h2
                          className="text-white font-extrabold leading-[1.08] tracking-tight drop-shadow-[0_2px_10px_rgba(0,0,0,0.75)]"
                          style={{ fontSize: 'clamp(28px, 4.6vw, 46px)' }}
                        >
                          {img.title}
                        </h2>
                      )}

                      {img.description && (
                        <p className="text-white mt-3.5 text-sm sm:text-base lg:text-lg leading-relaxed max-w-lg drop-shadow-[0_1px_6px_rgba(0,0,0,0.85)]">
                          {img.description}
                        </p>
                      )}

                      {/* No CTA here on purpose: "Explore Services" and "Contact
                          Office" already sit in the actions band directly beneath
                          the hero, and repeating them over the photograph read as
                          a duplicate. The caption stays purely descriptive. */}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        </div>

        {/* Prev / next arrows */}
        {heroImages.length > 1 && (
          <>
            <button
              onClick={goPrev}
              aria-label="Previous image"
              className="hidden sm:flex absolute left-4 lg:left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full items-center justify-center bg-black/30 hover:bg-black/55 backdrop-blur-md text-white ring-1 ring-white/25 transition-all duration-200 hover:scale-105 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <i className="ri-arrow-left-s-line text-xl" />
            </button>
            <button
              onClick={goNext}
              aria-label="Next image"
              className="hidden sm:flex absolute right-4 lg:right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full items-center justify-center bg-black/30 hover:bg-black/55 backdrop-blur-md text-white ring-1 ring-white/25 transition-all duration-200 hover:scale-105 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <i className="ri-arrow-right-s-line text-xl" />
            </button>
          </>
        )}

        {/* Slide indicators — let a visitor step through the UDA's images
            instead of only seeing whichever one the 6s timer is on. Hidden
            when there is nothing to page through. */}
        {heroImages.length > 1 && (
          <>
            <div className="absolute bottom-6 left-0 right-0 z-20 flex items-center justify-center gap-2">
              {heroImages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIndex(i)}
                  aria-label={`Show image ${i + 1} of ${heroImages.length}`}
                  aria-current={i === current}
                  className={`h-[6px] rounded-full cursor-pointer transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${
                    i === current ? 'w-8 bg-white' : 'w-2.5 bg-white/45 hover:bg-white/70'
                  }`}
                />
              ))}
            </div>

            {/* Slide counter */}
            <div className="hidden sm:block absolute bottom-6 right-6 z-20 text-white/55 text-xs font-semibold tabular-nums tracking-wide">
              {String(current + 1).padStart(2, '0')}
              <span className="text-white/25 mx-1">/</span>
              {String(heroImages.length).padStart(2, '0')}
            </div>

            {/* Dwell-time progress bar, re-keyed per slide so the CSS animation
                restarts and tracks the countdown to the next image. */}
            <div className="absolute bottom-0 left-0 right-0 z-20 h-[3px] bg-white/15">
              <div
                key={current}
                className="h-full hero-progress"
                style={{ background: `linear-gradient(90deg, ${accent}, #ffffff)` }}
              />
            </div>
          </>
        )}
      </section>

      {/* ====== 3. ACTIONS + STATS STRIP ====== */}
      {/* Both moved out from over the photograph and onto a white band beneath it,
          so the hero image is unobstructed. The cards no longer pull up into the
          hero (-mt-10 before) because that would collide with the slide dots. */}
      <section
        ref={statsReveal.ref as React.RefObject<HTMLElement>}
        className={`scroll-reveal bg-white border-b border-gray-100 ${statsReveal.isRevealed ? 'revealed' : ''}`}
      >
        <div className="reveal-content portal-container relative z-20 pt-8 sm:pt-10 pb-9">
          {/* CTAs — restyled for a light background; the on-image white/glass
              treatment they used to have is unreadable here. */}
          <div className="flex flex-col sm:flex-row justify-center gap-3 mb-8 sm:mb-10">
            <a
              href="#uda-services"
              className="group relative inline-flex items-center justify-center gap-2 overflow-hidden text-white px-7 py-3.5 font-semibold text-sm rounded-full transition-all duration-300 hover:-translate-y-0.5"
              style={{
                background: `linear-gradient(135deg, ${accent}, ${accentDark})`,
                boxShadow: `0 10px 26px ${accent}59`,
              }}
            >
              {/* Sheen sweep on hover */}
              <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
              <i className="ri-apps-2-line" /> Explore Services
              <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#uda-contact"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 font-semibold text-sm rounded-full border bg-white transition-all duration-300 hover:-translate-y-0.5"
              style={{ color: accentDark, borderColor: `${accent}59`, boxShadow: `inset 0 0 0 1px ${accent}1A` }}
            >
              <i className="ri-customer-service-2-line" /> Contact Office
            </a>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {stats.map((stat) => (
              <StatCard
                key={stat.label}
                stat={stat}
                accent={accent}
                accentDark={accentDark}
                run={statsReveal.isRevealed}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ====== 4. ABOUT THIS UDA ====== */}
      <section
        ref={aboutReveal.ref as React.RefObject<HTMLElement>}
        className={`scroll-reveal portal-section bg-[#FAFAF8] relative overflow-hidden ${aboutReveal.isRevealed ? 'revealed' : ''}`}
      >
        {/* Ambient accent blooms — depth without another photograph */}
        <span
          className="pointer-events-none absolute -top-24 -left-20 w-80 h-80 rounded-full blur-3xl opacity-[0.07]"
          style={{ background: accent }}
        />
        <span
          className="pointer-events-none absolute -bottom-28 -right-16 w-96 h-96 rounded-full blur-3xl opacity-[0.06]"
          style={{ background: accentDark }}
        />

        <div className="reveal-content portal-container relative grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-8 lg:gap-12 items-start">
          <div>
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-1 h-5 rounded-sm" style={{ background: accent }} />
              <span className="text-[11px] uppercase tracking-[2px] font-bold" style={{ color: accent }}>
                About the Authority
              </span>
            </div>
            <h2 className="text-2xl lg:text-[34px] font-extrabold text-[#1a1a1a] leading-tight mb-3">
              {theme.fullName}
            </h2>
            <div className="w-16 h-[3px] mb-5 rounded-full" style={{ background: `linear-gradient(90deg, ${accent}, ${accentDark})` }} />
            <p className="text-[15px] text-[#555] leading-[1.85] mb-4">
              The {theme.name} is constituted under the Andhra Pradesh Urban Areas
              (Development) Act to plan, regulate and promote the orderly growth of{' '}
              {theme.shortName} and its surrounding areas in the {theme.district} District
              of {theme.region}. Established in {theme.established}, the Authority oversees
              building permissions, layout approvals, master-plan implementation and
              public infrastructure within its jurisdiction.
            </p>
            <p className="text-[15px] text-[#555] leading-[1.85]">
              {theme.tagline}
            </p>

            {/* Mandate chips — what this Authority actually does, scannable */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mt-7">
              {focusAreas.map((f) => (
                <div
                  key={f.label}
                  className="stagger-child bg-white rounded-xl border border-gray-100 px-3 py-3.5 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_28px_rgba(0,0,0,0.08)]"
                >
                  <i className={`${f.icon} text-xl`} style={{ color: accent }} />
                  <div className="mt-1.5 text-[11.5px] font-semibold text-gray-700 leading-snug">{f.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick facts card — sticks alongside the prose on desktop */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_10px_34px_rgba(0,0,0,0.07)] overflow-hidden w-full lg:sticky lg:top-24">
            <div
              className="px-5 py-3.5 text-white font-semibold text-sm flex items-center gap-2"
              style={{ background: `linear-gradient(90deg, ${accent}, ${accentDark})` }}
            >
              <i className="ri-information-line" /> Authority at a Glance
            </div>
            <dl className="divide-y divide-gray-100">
              {glanceRows.map((row) => (
                <div
                  key={row.k}
                  className="flex items-center justify-between gap-3 px-5 py-3 transition-colors duration-200 hover:bg-gray-50"
                >
                  <dt className="flex items-center gap-2 text-[13px] text-gray-500 min-w-0">
                    <i className={`${row.icon} text-base flex-shrink-0`} style={{ color: accent }} />
                    <span className="truncate">{row.k}</span>
                  </dt>
                  <dd className="text-[13px] font-semibold text-gray-800 text-right">{row.v}</dd>
                </div>
              ))}
            </dl>
            <a
              href={mapsHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-5 py-3.5 text-[13px] font-bold border-t border-gray-100 transition-colors duration-200 hover:bg-gray-50"
              style={{ color: accentDark }}
            >
              <i className="ri-map-pin-line" /> Locate on Map
              <i className="ri-external-link-line text-xs" />
            </a>
          </div>
        </div>
      </section>

      {/* ====== 5. SHARED DATA-DRIVEN SECTIONS (load this UDA by siteId) ====== */}
      <div id="uda-services" className="scroll-mt-16">
        <KeyServicesSection />
      </div>
      <CitizenServicesSection />
      <AnnouncementSection />
      <GallerySection />

      {/* ====== 6. LOCAL CONTACT STRIP ====== */}
      <section
        id="uda-contact"
        className="portal-section scroll-mt-16 relative overflow-hidden"
        style={{ background: `linear-gradient(120deg, ${accentDark}, ${accent})` }}
      >
        {/* Light blooms so the flat gradient reads with some depth */}
        <span className="pointer-events-none absolute -top-24 left-1/4 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <span className="pointer-events-none absolute -bottom-32 right-0 w-[26rem] h-[26rem] rounded-full bg-black/15 blur-3xl" />

        <div className="portal-container relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="text-white">
              <span className="inline-flex items-center gap-1.5 bg-white/15 ring-1 ring-white/25 backdrop-blur-sm text-[11px] font-bold uppercase tracking-[2px] px-3 py-1.5 rounded-full">
                <i className="ri-customer-service-2-line" /> Get in touch
              </span>
              <h2 className="text-2xl lg:text-[32px] font-extrabold mt-4 mb-3 leading-tight">
                Reach the {theme.name} Office
              </h2>
              <p className="text-white/85 text-[15px] leading-relaxed max-w-lg">
                For building permissions, layout approvals, grievances or general
                enquiries within the {theme.district} jurisdiction, contact the
                Authority office or use the online services above.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <a
                  href="#uda-services"
                  className="group inline-flex items-center justify-center gap-2 bg-white px-6 py-3.5 font-semibold text-sm rounded-full hover:-translate-y-0.5 transition-all duration-300 shadow-[0_10px_26px_rgba(0,0,0,0.18)]"
                  style={{ color: accentDark }}
                >
                  <i className="ri-apps-2-line" /> Online Services
                  <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <button
                  onClick={onBackToDtcp}
                  className="inline-flex items-center justify-center gap-2 bg-white/10 ring-1 ring-white/30 text-white px-6 py-3.5 font-semibold text-sm rounded-full hover:bg-white/25 transition-all duration-300"
                >
                  <i className="ri-arrow-left-line" /> Back to DTCP Portal
                </button>
              </div>
            </div>

            {/* Contact cards are actionable — tap to navigate, call or mail */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                {
                  icon: 'ri-map-pin-2-fill',
                  label: 'Office',
                  value: `${theme.name}, ${theme.district}`,
                  href: mapsHref,
                  external: true,
                },
                {
                  icon: 'ri-phone-fill',
                  label: 'Phone',
                  value: phone,
                  href: `tel:${phone.replace(/[^\d+]/g, '')}`,
                  external: false,
                },
                {
                  icon: 'ri-mail-fill',
                  label: 'Email',
                  value: email,
                  href: `mailto:${email}`,
                  external: false,
                },
              ].map((c) => (
                <a
                  key={c.label}
                  href={c.href}
                  target={c.external ? '_blank' : undefined}
                  rel={c.external ? 'noopener noreferrer' : undefined}
                  className="group bg-white/10 backdrop-blur-md ring-1 ring-white/20 rounded-2xl p-4 text-white no-underline transition-all duration-300 hover:bg-white/20 hover:-translate-y-1"
                >
                  <span className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center mb-2.5 transition-transform duration-300 group-hover:scale-110">
                    <i className={`${c.icon} text-lg`} />
                  </span>
                  <div className="text-[11px] uppercase tracking-wide text-white/70">{c.label}</div>
                  <div className="text-[13px] font-semibold break-words mt-0.5">{c.value}</div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
